// Pieces of a run's Units tab (RunUnits.tsx): who supplies each part (the BOM's usual way, changed for this run only:
// migration 057), the BOM revision a product's builds use, and the opening balance of a run that started before it was
// recorded here; and its Orders tab (the make PO and every PO tied to the run, when each was ordered and received and
// how long that took, linking and unlinking, "Create make PO" from the units).

import { Link2, Plus, RefreshCw, Unlink } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { poPaid } from "../../../../../shared/bills.ts";
import type { BomItem } from "../../../../../shared/boms.ts";
import { revisionState } from "../../../../../shared/boms.ts";
import { numberOf } from "../../../../../shared/calc.ts";
import { componentsOf } from "../../../../../shared/consignment.ts";
import { type CostData, type PriceFlag, type RunUnits, roundParts, runPriceFlags, runResult, type Stack } from "../../../../../shared/costs.ts";
import { planToday } from "../../../../../shared/day.ts";
import { PO_KIND_LABELS, PO_STATUS_LABELS, PO_STATUSES, type PoStatus, poDates } from "../../../../../shared/po.ts";
import { columnLabel, variantColumns } from "../../../../../shared/projects.ts";
import {
  LAYER_LABELS,
  LAYERS,
  type Layer,
  openingOf,
  RUN_SUPPLIES,
  type RunSupply,
  runLabel,
  runPlanEditable,
  supplyWords,
} from "../../../../../shared/runs.ts";
import { dayOf, useBillIndex } from "../../../lib/bills.ts";
import { centsText, shortDate, usd4, useCostIndex } from "../../../lib/costs.ts";
import { money, qty } from "../../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../../lib/mutate.ts";
import { useRows } from "../../../lib/sync.ts";
import { Attachments } from "../../../ui/Attachments.tsx";
import { Badge, Button, cx, Input, Select } from "../../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../../ui/DataTable.tsx";
import { DialogLayer } from "../../../ui/DialogLayer.tsx";
import { Figure, Figures } from "../../../ui/PageParts.tsx";
import { Popover } from "../../../ui/Popover.tsx";
import { PaneLink, usePaneOpen } from "../../../ui/SidePane.tsx";
import { toast } from "../../../ui/toast.tsx";
import { Why, type WhyLine } from "../../../ui/Why.tsx";
import { Card } from "../Workspace.tsx";
import { PaperPoButton } from "./PastRunPaper.tsx";
import type { RunProps } from "./RunWorkspace.tsx";

/** What one unit is made of, as the lines of a "why" card. */
export function stackLines(s: Stack): WhyLine[] {
  return [
    ...LAYERS.filter((l) => s.cents[l] !== 0 || s.why[l].length).map((l) => ({
      label: LAYER_LABELS[l],
      value: centsText(s.cents[l]),
      detail: s.why[l]
        .slice(0, 4)
        .map((p) => `${p.label}${p.usd ? ` = ${usd4(p.usd)}` : ""}`)
        .concat(s.why[l].length > 4 ? [`and ${s.why[l].length - 4} more`] : [])
        .join(" · "),
    })),
    { label: "Landed cost per unit", value: centsText(s.totalCents), strong: true },
  ];
}

// Who supplies -------------------------------------------------------------------------------------------------

type PartSupply = { itemId: string; sku: string; name: string; now: Set<RunSupply>; usual: Set<RunSupply>; mine: boolean; settled: string };

/**
 * Who supplies each part of the run's builds (migration 057; Mitch: "I just don't think it should be static, this would
 * probably be per-run"): the BOM's usual way, changed here for this run only. The run's make orders (what HHL ships to
 * the CM, what's used up there) and its costs follow; a part is settled once units are reported built on a build using it.
 */
export function WhoSupplies({ run }: { run: RunProps["run"] }) {
  const cx_ = useCostIndex();
  const res = runResult(cx_, run.id);
  const vendors = useRows("vendor");
  const cm = vendors.find((v) => v.id === run.cm_vendor_id);
  const cmName = cm?.short_name || cm?.name || "";
  const editable = can("production_run.set_supply");
  const parts = useMemo(() => {
    const mine = cx_.supplyByRun.get(run.id);
    const by = new Map<string, PartSupply>();
    const variants = res ? res.variants.filter((v) => (res.units.get(v)?.planned ?? 0) > 0) : [];
    for (const v of variants.length ? variants : (res?.variants ?? []))
      for (const l of res?.planned.get(v)?.lines ?? []) {
        if (l.charge || l.service || !l.itemId) continue;
        const p = by.get(l.itemId) ?? { itemId: l.itemId, sku: l.sku, name: l.name, now: new Set(), usual: new Set(), mine: false, settled: "" };
        p.now.add(l.supply);
        p.usual.add(l.usual ?? l.supply);
        p.mine = mine?.has(l.itemId) ?? false;
        by.set(l.itemId, p);
      }
    // Settled: a make order of the run that uses it has units reported built.
    for (const po of cx_.posByRun.get(run.id) ?? [])
      for (const mo of cx_.makeOrdersByPo.get(po.id) ?? [])
        if (Number(mo.qty_output) > 0)
          for (const c of componentsOf(mo)) {
            const p = by.get(c.item_id);
            if (p && !p.settled) p.settled = `${po.number}: units reported built`;
          }
    return [...by.values()].sort((a, b) => Number(b.mine) - Number(a.mine) || a.sku.localeCompare(b.sku, undefined, { numeric: true }));
  }, [cx_, res, run.id]);
  const set = async (p: PartSupply, to: RunSupply) => {
    const back = p.usual.size === 1 && p.usual.has(to);
    const { ok } = await mutateWithResult("production_run.set_supply", { id: run.id, item_id: p.itemId, supply: back ? null : to });
    if (ok) toast.ok(back ? `${p.sku}: as its BOM says again` : `${p.sku}: ${supplyWords(to, cmName)} for ${run.number}`);
  };
  const usualText = (p: PartSupply) => [...p.usual].map((s) => supplyWords(s, cmName)).join(" · ");
  const changed = parts.filter((p) => p.mine).length;
  const openPane = usePaneOpen();
  const nowValue = (p: PartSupply) => (p.now.size === 1 ? [...p.now][0] : "");
  const nowText = (p: PartSupply) => {
    const v = nowValue(p);
    return v ? supplyWords(v, cmName) : "Mixed (several lines)";
  };
  const rows = parts.map((p) => ({ ...p, id: p.itemId }));
  // While some are changed for this run, they're a band of their own above the rest.
  const band = (p: PartSupply): TableSection =>
    p.mine ? { key: "mine", label: `Changed for ${run.number}`, order: 0 } : { key: "bom", label: "As its BOM says", order: 1 };
  const columns: Column<PartSupply & { id: string }>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "150px",
      cell: (p) => (
        <PaneLink kind="item" id={p.itemId} to={`/parts?open=${p.itemId}`} className="truncate font-mono text-[12px] hover:underline">
          {p.sku}
        </PaneLink>
      ),
    },
    { key: "name", header: "Name", width: "minmax(140px,1fr)", cell: (p) => <span className="truncate">{p.name}</span> },
    {
      key: "now",
      header: "This run",
      headerTitle: `Who supplies it for ${run.number}: click to change it`,
      width: "240px",
      value: nowText,
      cell: (p) => (
        <span className="flex min-w-0 items-center gap-1.5" title={p.settled ? `Settled (${p.settled})` : undefined}>
          <span className="truncate text-[12px]">{nowText(p)}</span>
          {p.mine && <Badge tone="accent">this run</Badge>}
        </span>
      ),
      edit: {
        options: (p) => [
          ...(nowValue(p) ? [] : [{ value: "", label: "Mixed (several lines)" }]),
          ...RUN_SUPPLIES.map((s) => ({ value: s, label: supplyWords(s, cmName) })),
        ],
        get: nowValue,
        save: (p, to) => {
          if (to) set(p, to as RunSupply);
        },
        can: (p) => !p.settled,
      },
    },
    {
      key: "usual",
      header: "Usually (its BOM)",
      width: "minmax(160px,1fr)",
      value: usualText,
      cell: (p) => (
        <span className="flex min-w-0 items-center gap-2 text-[12px] text-muted">
          <span className="truncate">{usualText(p)}</span>
          {p.mine && editable && !p.settled && (
            <button
              type="button"
              className="shrink-0 text-accent-text hover:underline"
              onClick={async (e) => {
                e.stopPropagation();
                if ((await mutateWithResult("production_run.set_supply", { id: run.id, item_id: p.itemId, supply: null })).ok)
                  toast.ok(`${p.sku}: as its BOM says again`);
              }}
            >
              Back to the BOM's
            </button>
          )}
        </span>
      ),
    },
  ];
  return (
    <Card
      boxed={false}
      title="Who supplies each part"
      note={`Its BOM says who usually does; change a part for ${run.number} only${changed ? ` (${changed} changed)` : ""}. Its make orders, what HHL ships to ${cmName || "the CM"} and its costs follow`}
      foot={<>A part is settled for this run once {cmName || "the CM"} reports units built on a build that uses it.</>}
    >
      <DataTable
        fit
        rows={rows}
        columns={columns}
        sectionOf={changed ? band : undefined}
        editing={editable}
        onOpen={(p) => openPane("item", p.itemId)}
        exportName="Who supplies"
        pageFilters={false}
        rowNoun={["part", "parts"]}
        empty="Type units in the Units table to see the parts its builds need."
      />
    </Card>
  );
}

/** The BOM revision a product's builds on the run use (`label`: the product, when the run makes more than one). */
export function BomPicker({
  run,
  variants,
  productKey,
  disabled,
  label,
}: {
  run: RunProps["run"];
  variants: BomItem[];
  productKey: string;
  disabled?: boolean;
  label?: string;
}) {
  const cx_ = useCostIndex();
  const revs = cx_.ix.families.get(productKey) ?? [];
  const lines = (cx_.linesByRun.get(run.id) ?? []).filter((l) => variants.some((v) => v.id === l.item_id));
  const chosen = lines.find((l) => l.bom_id)?.bom_id ?? "";
  if (!revs.length) return <span className="text-[12px] text-warn">{label ? `${label}: no BOM yet` : "No BOM yet"}</span>;
  return (
    <div className="flex items-center gap-1.5 text-[12px]">
      <span className="max-w-40 truncate text-muted" title={label}>
        {label ? `${label} BOM` : "BOM"}
      </span>
      <div className="w-44">
        <Select
          value={chosen}
          disabled={disabled || !lines.length}
          options={[{ value: "", label: "Current revision" }, ...revs.map((b) => ({ value: b.id, label: `Rev ${b.revision} (${revisionState(cx_.ix, b)})` }))]}
          onChange={(e) => mutate("production_run.set_bom", { id: run.id, item_ids: lines.map((l) => l.item_id), bom_id: e.target.value || null })}
          className="h-7"
        />
      </div>
    </div>
  );
}

// Orders -------------------------------------------------------------------------------------------------------

export function OrdersTab({ run, scope }: RunProps) {
  const cx_ = useCostIndex();
  const pos = [...(cx_.posByRun.get(run.id) ?? [])].sort(
    (a, b) => Number(b.kind === "make") - Number(a.kind === "make") || a.number.localeCompare(b.number, undefined, { numeric: true }),
  );
  const lines = (cx_.linesByRun.get(run.id) ?? []).filter((l) => Number(l.qty) > 0);
  const make = pos.filter((p) => p.kind === "make" && p.status !== "cancelled");
  const allPos = useRows("po");
  const createMake = async () => {
    if (!run.cm_vendor_id) return toast.error("Choose the run's CM in its Basics first");
    const id = crypto.randomUUID();
    const { ok, result } = await mutateWithResult("po.create_make", {
      id,
      vendor_id: run.cm_vendor_id,
      production_run_id: run.id,
      ...(run.target_ship ? { expected_ship: run.target_ship } : {}),
      ...(run.target_arrive ? { expected_arrive: run.target_arrive } : {}),
      notes: `${runLabel(run)}`,
      lines: lines.map((l) => ({ id: crypto.randomUUID(), item_id: l.item_id, qty: Number(l.qty), ...(l.bom_id ? { bom_id: l.bom_id } : {}) })),
    });
    const r = result as { number?: string; warnings?: string[] } | undefined;
    if (ok) toast.ok(`${r?.number ?? "The make PO"} drafted from the units (${lines.length} builds)${r?.warnings?.length ? `: ${r.warnings[0]}` : ""}`);
  };
  // Accounted for --- each PO paid, and by what (Mitch: "ensure that payments are accounted for and linked").
  const bix = useBillIndex();
  const paidOf = new Map(pos.map((po) => [po.id, poPaid(bix, po.id, dayOf)]));
  const live = pos.filter((p) => p.status !== "cancelled" && p.status !== "draft");
  const owed = live.reduce((n, p) => n + (p.currency === "USD" ? (paidOf.get(p.id)?.total ?? 0) : 0), 0);
  const paidUsd = live.reduce((n, p) => n + (p.currency === "USD" ? Math.min(paidOf.get(p.id)?.paid ?? 0, paidOf.get(p.id)?.total ?? 0) : 0), 0);
  const notPaid = live.filter((p) => paidOf.get(p.id)?.state !== "paid");
  const openPane = usePaneOpen();
  // When each was ordered and received, and how long it took (shared/po.ts poDates(): migration 078's dates on paper,
  // else when it was sent and its last receipt here)
  const poById = new Map(allPos.map((p) => [p.id, p]));
  const receipts = useRows("receipt");
  // A cancelled PO stays listed, faded.
  const dim = (po: RunPo, node: ReactNode) => (
    <span className={cx("flex min-w-0 items-center gap-1.5", po.status === "cancelled" && "opacity-50")}>{node}</span>
  );
  const rows: PoRow[] = pos.map((po) => {
    const ls = cx_.linesByPo.get(po.id) ?? [];
    const vendor = cx_.vendorById.get(po.vendor_id);
    return {
      id: po.id,
      po,
      lines: ls.length,
      total: ls.reduce((n, l) => n + Number(l.qty) * Number(l.unit_price ?? 0), 0),
      ordered: ls.reduce((n, l) => n + Number(l.qty), 0),
      received: ls.reduce((n, l) => n + Number(l.qty_received), 0),
      vendor: vendor ? vendor.short_name || vendor.name : "",
      when: (() => {
        const full = poById.get(po.id);
        return full
          ? poDates(
              full,
              receipts.filter((x) => x.po_id === po.id),
            )
          : { ordered: po.sent_on ?? null, received: null, days: null };
      })(),
      paid: paidOf.get(po.id) ?? null,
    };
  });
  // Ordered and Arrived are typed in their cells: the dates on paper (sent_on, received_on), never a send or a receipt.
  // Emptied, a PO sent or received here shows that day again.
  const datesOk = can("po.set_history");
  const dateEdit = (key: "sent_on" | "received_on", get: (r: PoRow) => string | null) => ({
    type: "date" as const,
    get: (r: PoRow) => get(r) ?? "",
    save: (r: PoRow, text: string) => mutate("po.set_history", { id: r.po.id, [key]: text || null }),
    can: (r: PoRow) => r.po.status !== "cancelled",
  });
  const columns: Column<PoRow>[] = [
    {
      key: "po",
      header: "PO",
      width: "100px",
      // The make PO first, then by number
      value: (r) => `${r.po.kind === "make" ? 0 : 1}:${r.po.number}`,
      text: (r) => r.po.number,
      cell: (r) =>
        dim(
          r.po,
          <PaneLink kind="po" id={r.po.id} to={`/pos?open=${r.po.id}`} className="font-mono text-[12px] font-medium hover:underline">
            {r.po.number}
          </PaneLink>,
        ),
    },
    {
      key: "vendor",
      header: "Vendor",
      width: "minmax(140px,1fr)",
      value: (r) => r.vendor,
      cell: (r) => dim(r.po, <span className="min-w-0 truncate">{r.vendor}</span>),
    },
    {
      key: "kind",
      header: "Kind",
      width: "80px",
      value: (r) => PO_KIND_LABELS[r.po.kind],
      cell: (r) => dim(r.po, <span className="text-[12px]">{PO_KIND_LABELS[r.po.kind]}</span>),
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      value: (r) => PO_STATUSES.indexOf(r.po.status as PoStatus),
      text: (r) => PO_STATUS_LABELS[r.po.status as PoStatus],
      cell: (r) =>
        dim(
          r.po,
          <Badge tone={r.po.status === "draft" ? "neutral" : r.po.status === "received" || r.po.status === "closed" ? "ok" : "accent"}>
            {PO_STATUS_LABELS[r.po.status as PoStatus]}
          </Badge>,
        ),
    },
    {
      key: "ordered_on",
      header: "Ordered",
      headerTitle: "The day it went to the vendor (on paper, or when it was marked sent here). Click to type it",
      width: "128px",
      value: (r) => r.when.ordered,
      text: (r) => r.when.ordered ?? "",
      cell: (r) => dim(r.po, r.when.ordered ? <span className="text-[12px]">{shortDate(r.when.ordered)}</span> : <span className="text-faint">—</span>),
      // Typed in place (Mitch, 2026-11-04: "I do not see anywhere to edit those"): the day on paper, po.set_history
      ...(datesOk ? { edit: dateEdit("sent_on", (r) => r.when.ordered) } : {}),
    },
    {
      key: "received_on",
      header: "Arrived",
      headerTitle: "The day it all arrived (on paper, or its last receipt here). Click to type it",
      width: "128px",
      value: (r) => r.when.received,
      text: (r) => r.when.received ?? "",
      cell: (r) => dim(r.po, r.when.received ? <span className="text-[12px]">{shortDate(r.when.received)}</span> : <span className="text-faint">—</span>),
      ...(datesOk ? { edit: dateEdit("received_on", (r) => r.when.received) } : {}),
    },
    {
      key: "took",
      header: "Took",
      headerTitle: "Days from ordered to arrived: open the PO to make it the vendor's lead time",
      width: "80px",
      align: "right",
      value: (r) => r.when.days,
      text: (r) => r.when.days ?? "",
      cell: (r) => dim(r.po, r.when.days != null ? <span className="font-mono text-[12px]">{r.when.days} d</span> : <span className="text-faint">—</span>),
    },
    { key: "lines", header: "Lines", width: "70px", align: "right", cell: (r) => dim(r.po, <span className="font-mono text-[12px]">{r.lines}</span>) },
    {
      key: "total",
      header: "Total",
      width: "120px",
      align: "right",
      value: (r) => Math.round(r.total * 100) / 100,
      text: (r) => money(Math.round(r.total * 100) / 100, r.po.currency),
      cell: (r) => dim(r.po, <span className="font-mono text-[12px]">{money(Math.round(r.total * 100) / 100, r.po.currency)}</span>),
    },
    {
      key: "received",
      header: "Received",
      width: "110px",
      align: "right",
      text: (r) => `${qty(r.received)} / ${qty(r.ordered)}`,
      cell: (r) =>
        dim(
          r.po,
          <span className="font-mono text-[12px]" title={`${qty(r.received)} of ${qty(r.ordered)} received`}>
            {qty(r.received)} / {qty(r.ordered)}
          </span>,
        ),
    },
    {
      key: "paid",
      header: "Paid",
      width: "minmax(150px,1fr)",
      value: (r) => paidWords(r.paid, r.po.status === "draft"),
      cell: (r) => dim(r.po, <PaidCell paid={r.paid} draft={r.po.status === "draft"} />),
    },
    {
      key: "files",
      header: "Files",
      width: "minmax(120px,180px)",
      value: () => "",
      cell: (r) => dim(r.po, <Attachments owner={{ type: "po", id: r.po.id }} compact />),
    },
    ...(can("po.link_run")
      ? [
          {
            key: "unlink",
            header: "",
            width: "44px",
            value: () => "",
            cell: (r: PoRow) => (
              <button
                type="button"
                title={`Take ${r.po.number} off this run`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(`Take ${r.po.number} off ${run.number}? Its costs stop counting on this run.`))
                    mutate("po.link_run", { po_id: r.po.id, run_id: null });
                }}
                className="rounded p-1 text-faint hover:bg-hover hover:text-text"
              >
                <Unlink className="size-3.5" />
              </button>
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      <Card
        boxed={false}
        title="POs on this run"
        note="The make PO (its make orders are the run's builds) and every PO bought for it: parts, molds and tooling, freight, services. A PO is on one run at most."
        actions={
          <>
            {can("po.create_make") && !make.length && !run.historical && (
              <Button
                size="sm"
                variant="primary"
                icon={<Plus className="size-3.5" />}
                disabled={!lines.length || run.status === "closed"}
                onClick={createMake}
                title="A draft make PO to the run's CM, one build per variant in the plan"
              >
                Create make PO from the plan
              </Button>
            )}
            {/* A past run takes its POs from paper any time */}
            {run.historical && <PaperPoButton run={run} />}
            {can("po.link_run") && (run.status !== "closed" || run.historical) && <LinkPo run={run} pos={allPos} />}
          </>
        }
      >
        <DataTable
          fit
          rows={rows}
          columns={columns}
          onOpen={(r) => openPane("po", r.po.id)}
          editing={datesOk}
          exportName="Run POs"
          pageFilters={false}
          rowNoun={["PO", "POs"]}
          empty={
            run.historical
              ? "No POs yet. Record the make PO and the parts POs from paper (the PI or invoice), or link POs already in HHL Ops."
              : "No POs yet. Create the make PO from the plan, then link the parts POs bought for this run (or link POs from their own page)."
          }
        />
        {live.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 border-t border-line px-4 py-2 text-[12px]">
            <span className="font-medium">
              Paid for: {money(Math.round(paidUsd * 100) / 100, "USD")} of {money(Math.round(owed * 100) / 100, "USD")}
            </span>
            {notPaid.length ? (
              <span className="text-warn">
                {notPaid.length} {notPaid.length === 1 ? "PO" : "POs"} not paid in full here: {notPaid.map((p) => p.number).join(", ")}. Open each one's bill
                from the PO's Paid to record how, or link its Wise transfer in Bills & payments → Wise history.
              </span>
            ) : (
              <span className="text-ok">every PO paid, and how</span>
            )}
          </div>
        )}
      </Card>
      <SubBuilds run={run} scopeVariants={scope.products.flatMap((p) => p.variants.map((v) => v.id))} />
    </>
  );
}

type RunPo = CostData["pos"][number];
type PoRow = {
  id: string;
  po: RunPo;
  lines: number;
  total: number;
  ordered: number;
  received: number;
  vendor: string;
  when: ReturnType<typeof poDates>;
  paid: ReturnType<typeof poPaid> | null;
};

/** How a PO was paid, in the words PaidCell shows (for sorting, search and Copy / CSV). */
function paidWords(paid: ReturnType<typeof poPaid> | null, draft: boolean) {
  if (!paid || draft) return "";
  const last = paid.payments.at(-1);
  const how = last ? `${last.how}${last.on ? ` · ${shortDate(last.on)}` : ""}` : "";
  if (paid.state === "paid") return `Paid · ${how}`;
  if (paid.state === "part")
    return `${money(Math.round(paid.paid * 100) / 100, paid.currency)} of ${money(Math.round(paid.total * 100) / 100, paid.currency)} paid`;
  return paid.state === "billed" ? "Billed, not paid" : "Not paid here";
}

/** Accounted for --- a PO's payment: "Paid · Wise Aug 5", "Part paid $500", "Billed, not paid", "Not paid here". */
function PaidCell({ paid, draft }: { paid: ReturnType<typeof poPaid> | null; draft: boolean }) {
  if (!paid || draft) return <span className="text-[12px] text-faint">—</span>;
  const title = paid.payments.map((p) => `${p.number}: ${p.how}${p.on ? `, ${p.on}` : ""}`).join("\n") || undefined;
  const words = paidWords(paid, draft);
  if (paid.state === "paid" || paid.state === "part")
    return (
      <span className={cx("min-w-0 truncate text-[12px]", paid.state === "paid" ? "text-ok" : "text-warn")} title={title}>
        {words}
      </span>
    );
  return <span className="text-[12px] text-warn">{words}</span>;
}

type SubRow = { id: string; sku: string; name: string; needed: number; ordered: number };

const SUB_COLUMNS: Column<SubRow>[] = [
  { key: "sku", header: "SKU", width: "150px", cell: (r) => <span className="truncate font-mono text-[12px]">{r.sku}</span> },
  { key: "name", header: "Name", width: "minmax(160px,1fr)", cell: (r) => <span className="truncate">{r.name}</span> },
  {
    key: "needed",
    header: "Needed",
    width: "110px",
    align: "right",
    text: (r) => r.needed,
    cell: (r) => <span className="font-mono text-[12px]">{qty(r.needed)} needed</span>,
  },
  {
    key: "ordered",
    header: "On make POs",
    headerTitle: "Units on this run's make POs",
    width: "220px",
    align: "right",
    text: (r) => r.ordered,
    cell: (r) => (
      <span className={cx("truncate text-[12px]", r.ordered ? "text-ok" : "text-warn")}>
        {r.ordered ? `${qty(r.ordered)} on this run's make POs` : "Not on a make PO of this run"}
      </span>
    ),
  },
];

/** Stocked sub-assemblies the plan's builds use (main PCBAs), and whether a make PO on the run builds them. */
function SubBuilds({ run, scopeVariants }: { run: RunProps["run"]; scopeVariants: string[] }) {
  const cx_ = useCostIndex();
  const res = runResult(cx_, run.id);
  const needs = useMemo(() => {
    const out = new Map<string, number>();
    if (!res) return out;
    for (const v of res.variants) {
      const u = res.units.get(v)?.planned ?? 0;
      if (!u || !scopeVariants.includes(v)) continue;
      const made = new Set(
        res.planned
          .get(v)
          ?.lines.filter((l) => l.via)
          .map((l) => l.via.split(" › ")[0]) ?? [],
      );
      for (const sku of made) {
        const item = cx_.d.items.find((i) => i.sku === sku);
        if (item) out.set(item.id, (out.get(item.id) ?? 0) + u);
      }
    }
    return out;
  }, [cx_, res, scopeVariants]);
  if (!needs.size || !res) return null;
  const rows: SubRow[] = [...needs].map(([id, n]) => {
    const item = cx_.itemById.get(id);
    return { id, sku: item?.sku ?? "", name: item?.name ?? "", needed: n, ordered: res.units.get(id)?.ordered ?? 0 };
  });
  return (
    <Card
      boxed={false}
      title="Sub-assemblies these builds use"
      note="Built at a CM as their own make orders (usually a make PO shipping to the CM); link it here so their cost counts on this run"
    >
      <DataTable fit rows={rows} columns={SUB_COLUMNS} exportName="Run sub-assemblies" pageFilters={false} rowNoun={["sub-assembly", "sub-assemblies"]} />
    </Card>
  );
}

function LinkPo({ run, pos }: { run: RunProps["run"]; pos: ReturnType<typeof useRows<"po">> }) {
  const [query, setQuery] = useState("");
  const vendors = useRows("vendor");
  const runs = useRows("production_run");
  const name = (id: string) => {
    const v = vendors.find((x) => x.id === id);
    return v ? v.short_name || v.name : "";
  };
  const q = query.trim().toLowerCase();
  const shown = pos
    .filter((p) => p.production_run_id !== run.id && p.status !== "cancelled")
    .filter((p) => !q || p.number.toLowerCase().includes(q) || name(p.vendor_id).toLowerCase().includes(q) || p.vendor_ref.toLowerCase().includes(q))
    .sort((a, b) => Number(!!a.production_run_id) - Number(!!b.production_run_id) || b.created_at.localeCompare(a.created_at))
    .slice(0, 30);
  return (
    <Popover
      width={420}
      button={(toggle) => (
        <Button size="sm" icon={<Link2 className="size-3.5" />} onClick={toggle}>
          Link a PO
        </Button>
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-2">
          <Input autoFocus value={query} placeholder="PO number, vendor or their ref…" onChange={(e) => setQuery(e.target.value)} />
          <div className="max-h-80 overflow-y-auto">
            {shown.map((p) => {
              const other = runs.find((r) => r.id === p.production_run_id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={async () => {
                    if (other && !confirm(`${p.number} is on ${other.number}. Move it to ${run.number}?`)) return;
                    if (await mutate("po.link_run", { po_id: p.id, run_id: run.id, move: !!other })) close();
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-hover"
                >
                  <span className="w-20 font-mono text-[12px]">{p.number}</span>
                  <span className="min-w-0 flex-1 truncate">{name(p.vendor_id)}</span>
                  <span className="text-[11px] text-muted">{PO_STATUS_LABELS[p.status]}</span>
                  {other && <span className="text-[11px] text-warn">on {other.number}</span>}
                </button>
              );
            })}
            {!shown.length && <div className="px-2 py-2 text-muted">No PO matches.</div>}
          </div>
          <div className="text-[11px] text-faint">A PO is on one run at most; linking one from another run moves it. Nothing the vendor sees changes.</div>
        </div>
      )}
    </Popover>
  );
}

// Opening balance -----------------------------------------------------------------------------------------

export function OpeningDialog({ run, onClose }: { run: RunProps["run"]; onClose: () => void }) {
  const cx_ = useCostIndex();
  const makes = (cx_.posByRun.get(run.id) ?? []).filter((p) => p.kind === "make" && p.status !== "cancelled" && p.status !== "draft");
  const orders = makes.flatMap((po) => (cx_.makeOrdersByPo.get(po.id) ?? []).map((mo) => ({ mo, po })));
  const [asOf, setAsOf] = useState(planToday);
  const [lines, setLines] = useState<Record<string, { built: string; shipped: string; received: string; rejected: string }>>({});
  const [parts, setParts] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const consigned = useMemo(() => {
    const out = new Map<string, { sku: string; name: string }>();
    for (const { mo } of orders)
      for (const c of componentsOf(mo as never))
        if (c.supply === "hhl_consigns" && c.kind !== "service" && c.source === "local") out.set(c.item_id, { sku: c.sku, name: c.name });
    return [...out].sort((a, b) => a[1].sku.localeCompare(b[1].sku));
  }, [orders]);
  const n = (s: string | undefined) => numberOf(s);
  const partRows = consigned.map(([id, c]) => ({ id, ...c }));
  const partColumns: Column<(typeof partRows)[number]>[] = [
    { key: "sku", header: "SKU", width: "minmax(120px,1fr)", cell: (p) => <span className="truncate font-mono text-[12px]">{p.sku}</span> },
    { key: "name", header: "Name", width: "minmax(160px,2fr)", cell: (p) => <span className="truncate text-[12px] text-muted">{p.name}</span> },
    {
      key: "count",
      header: "At the CM",
      width: "110px",
      align: "right",
      value: (p) => parts[p.id] ?? "",
      cell: (p) => (parts[p.id]?.trim() ? <span className="font-mono text-[12px]">{parts[p.id]}</span> : <span className="text-faint">—</span>),
      edit: { get: (p) => parts[p.id] ?? "", save: (p, text) => setParts((was) => ({ ...was, [p.id]: text })) },
    },
  ];
  const save = async () => {
    const ok = await mutate("production_run.record_opening", {
      id: run.id,
      as_of: asOf,
      lines: orders
        .filter(({ mo }) => lines[mo.id] && n(lines[mo.id].built) > 0)
        .map(({ mo }) => ({
          make_order_id: mo.id,
          built: n(lines[mo.id].built),
          shipped: n(lines[mo.id].shipped),
          received: n(lines[mo.id].received),
          rejected: n(lines[mo.id].rejected),
        })),
      parts: Object.entries(parts)
        .filter(([, v]) => v.trim() !== "")
        .map(([item_id, v]) => ({ item_id, qty: numberOf(v) })),
      notes,
    });
    if (ok) {
      toast.ok(`Opening balance as of ${asOf} recorded`);
      onClose();
    }
  };
  const field = (id: string, k: "built" | "shipped" | "received" | "rejected") => (
    <Input
      inputMode="numeric"
      value={lines[id]?.[k] ?? ""}
      placeholder="0"
      onChange={(e) => setLines({ ...lines, [id]: { ...(lines[id] ?? { built: "", shipped: "", received: "", rejected: "" }), [k]: e.target.value } })}
      className="h-7 w-20 text-right font-mono"
    />
  );
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-[760px] rounded-xl border border-line bg-surface p-4 shadow-pop">
        <div className="text-[15px] font-semibold">Opening balance for {run.number}</div>
        <div className="mt-1 text-[12px] text-muted">
          What had already happened before this run was recorded in HHL Ops. It's posted through the ledger's own rules: the units built become output reports
          dated then (their parts consumed by the BOM snapshot), the parts at the CM become an opening count at its site, and units already received go on the
          PO lines (no receipt: they're in stock and Shopify already). It can be recorded once.
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-[12px] text-muted">As of</span>
          <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-8 w-40" />
        </div>
        <div className="mt-3 rounded-md border border-line">
          <div className="grid grid-cols-[90px_minmax(0,1fr)_repeat(4,88px)] gap-x-2 px-3 py-1.5 text-[11px] text-faint uppercase">
            <span>PO</span>
            <span>Build</span>
            <span className="text-right">Built</span>
            <span className="text-right">Shipped</span>
            <span className="text-right">Received</span>
            <span className="text-right">Rejected</span>
          </div>
          {orders.map(({ mo, po }) => (
            <div key={mo.id} className="grid grid-cols-[90px_minmax(0,1fr)_repeat(4,88px)] items-center gap-x-2 border-t border-line px-3 py-1">
              <span className="font-mono text-[12px]">{po.number}</span>
              <span className="truncate">{cx_.itemById.get(mo.item_id)?.sku}</span>
              {(["built", "shipped", "received", "rejected"] as const).map((k) => (
                <span key={k} className="flex justify-end">
                  {field(mo.id, k)}
                </span>
              ))}
            </div>
          ))}
          {!orders.length && <div className="border-t border-line px-3 py-2 text-muted">The run has no sent make PO yet (record it as sent first).</div>}
        </div>
        {consigned.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-[12px] font-medium">Parts at the CM then (an opening count; leave blank what wasn't counted)</span>
            </div>
            {/* Click a count to type it; Enter saves it and goes on to the next part */}
            <DataTable fit={8} editing rows={partRows} columns={partColumns} exportName="Opening count" pageFilters={false} rowNoun={["part", "parts"]} />
          </div>
        )}
        <div className="mt-3">
          <Input value={notes} placeholder="Where these numbers come from (e.g. Tailhoo's report of Oct 1)" onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={!orders.length && !Object.values(parts).some((v) => v.trim())}>
            Record opening balance
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}
